'use client';

import { gql } from '@apollo/client';
import React, { useEffect } from 'react';

import {
  creditScoreBracketName,
  getCreditScoreBracket,
} from '@components/transunion-flow/utils';
import { TuVantageScore3Buckets } from '@generated-gql/generated-types';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { useEmitRewardableEvent } from '@lib/hooks/nerdwallet-plus';
import { getLogger } from '@lib/logging';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';

const logger = getLogger('Credit Score Rewards Emitter');

const QUERY = gql`
  query GetCreditScoreRewardsEmitterData {
    tuCreditReportIdentityProfile {
      vantageScore3
      vantageScore3Bucket
      vantageScore3Delta
    }
  }
`;

// Component to emit rewardable events. This allows the parent component to remain a server
// component and avoids loading states
const RewardsEmitter: React.FC = () => {
  const [emitReward] = useEmitRewardableEvent();

  useEffect(() => {
    emitReward(RewardableEventType.VIEWED_CREDIT_SCORE);
  }, [emitReward]);

  const { data } = useQueryWithLog(logger, QUERY);
  const { vantageScore3, vantageScore3Bucket, vantageScore3Delta } =
    data?.tuCreditReportIdentityProfile || {};
  useEffect(() => {
    if (vantageScore3 && vantageScore3Delta) {
      const previousScoreBucket = getCreditScoreBracket(
        vantageScore3 - vantageScore3Delta,
      ).name;

      if (previousScoreBucket === creditScoreBracketName.POOR) {
        switch (vantageScore3Bucket) {
          case TuVantageScore3Buckets.Average:
            emitReward(RewardableEventType.BOOSTED_CS_POOR_AVERAGE);
            break;
          case TuVantageScore3Buckets.Good:
            emitReward(RewardableEventType.BOOSTED_CS_POOR_AVERAGE);
            emitReward(RewardableEventType.BOOSTED_CS_AVERAGE_GOOD);
            break;
          case TuVantageScore3Buckets.Excellent:
            emitReward(RewardableEventType.BOOSTED_CS_POOR_AVERAGE);
            emitReward(RewardableEventType.BOOSTED_CS_AVERAGE_GOOD);
            emitReward(RewardableEventType.BOOSTED_CS_GOOD_EXCELLENT);
            break;
        }
      } else if (previousScoreBucket === creditScoreBracketName.AVERAGE) {
        switch (vantageScore3Bucket) {
          case TuVantageScore3Buckets.Good:
            emitReward(RewardableEventType.BOOSTED_CS_AVERAGE_GOOD);
            break;
          case TuVantageScore3Buckets.Excellent:
            emitReward(RewardableEventType.BOOSTED_CS_AVERAGE_GOOD);
            emitReward(RewardableEventType.BOOSTED_CS_GOOD_EXCELLENT);
            break;
        }
      } else if (previousScoreBucket === creditScoreBracketName.GOOD) {
        switch (vantageScore3Bucket) {
          case TuVantageScore3Buckets.Excellent:
            emitReward(RewardableEventType.BOOSTED_CS_GOOD_EXCELLENT);
            break;
        }
      }
    }
  });

  return null;
};

export default RewardsEmitter;
